import React from "react";
import {
  Box,
  Chip,
  Divider,
  MenuItem,
  Typography,
  Skeleton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  AutomationsOutline,
  MoreHorizontal,
  User,
  Refresh02,
} from "@tiny/icons";
import { Handle, NodeProps, Position } from "reactflow";
import { NodeData, LogicNodeStats } from "../types";
import { MenuIconButton } from "../../../../components";
import useDeleteNode from "../Hooks/useDeleteNode";
import DeleteBranchDialog from "../Hooks/DeleteBranchDialog";
import ConditionalSplitForm from "../../../../customComponents/ConditionalSplitForm";
import { useDrawerInvokers } from "@tiny/lib";
import { transformNodeToFormValues } from "../../../../customComponents/ConditionalSplitForm/ConditionalSplitForm.hooks";
import { useWorkflowsBuilderPageView } from "../../WorkflowsBuilderPage.context";
import { formatNumber } from "@tiny/utils/formatUtils";

const LogicNode = ({ id, data }: NodeProps<NodeData>) => {
  const { deleteNode, deleteBranch } = useDeleteNode();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleConfirmDelete = (option: "yes" | "no" | "both") => {
    setDialogOpen(false);
    if (option === "yes") deleteBranch(id, "left");
    else if (option === "no") deleteBranch(id, "right");
    else deleteNode(id);
  };
  const { configured } = data.config;
  const { showDrawer } = useDrawerInvokers();
  const {
    viewMode,
    isStatsLoading,
    getWorkflowStatsError,
    refetchWorkflowStats,
    workflowStats,
    showNodeStats,
  } = useWorkflowsBuilderPageView();

  const nodeStats = workflowStats?.[id] as LogicNodeStats | undefined;

  const handleClick = (e: React.SyntheticEvent) => {
    showDrawer({
      children: <ConditionalSplitForm id={id} />,
    });
  };

  const handleRefreshStats = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    refetchWorkflowStats();
  };

  const renderStatsContent = () => {
    if (getWorkflowStatsError) {
      return (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Typography variant="label2" fontSize={12} color="error">
            Failed to load stats
          </Typography>
          <Button
            size="small"
            startIcon={<Refresh02 sx={{ fontSize: 12 }} />}
            onClick={handleRefreshStats}
            sx={{ minWidth: "auto", fontSize: 12 }}
          >
            Reload
          </Button>
        </Box>
      );
    }

    if (isStatsLoading) {
      return (
        <Box display="flex" justifyContent="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Box>
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={30} height={14} />
            </Box>
          </Box>
        </Box>
      );
    }

    const positiveUsers = formatNumber(nodeStats?.positive?.users || 0);
    const negativeUsers = formatNumber(nodeStats?.negative?.users || 0);

    return (
      <Tooltip
        title={`${positiveUsers} users took the "Yes" path, ${negativeUsers} users took the "No" path`}
        componentsProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                padding: 4,
              },
            },
          },
        }}
        arrow
      >
        <Box display="flex" justifyContent="center">
          <Box display="flex" alignItems="center" gap={1}>
            <User sx={{ fontSize: 16, color: "grey.80" }} />
            <Typography variant="caption" color="grey.100" component="div">
              <strong>Users Split</strong>
              <Typography color="grey.80" component="span" ml={1}>
                Yes: {positiveUsers} | No: {negativeUsers}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const renderPathStats = (
    pathType: "positive" | "negative",
    label: string
  ) => {
    if (getWorkflowStatsError) {
      return null;
    }

    if (isStatsLoading) {
      return (
        <Box display="flex" justifyContent="center" mt={1}>
          <Skeleton variant="text" width={30} height={14} />
        </Box>
      );
    }

    const users =
      pathType === "positive"
        ? formatNumber(nodeStats?.positive?.users || 0)
        : formatNumber(nodeStats?.negative?.users || 0);

    return (
      <Box display="flex" justifyContent="center" mt={1}>
        <Typography variant="caption" color="grey.80" fontSize={11}>
          {users}
        </Typography>
      </Box>
    );
  };

  transformNodeToFormValues(data);

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ backgroundColor: "transparent", border: "none" }}
      />
      <Box>
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[80]}`,
            padding: 3,
            paddingBottom: 4,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            width: "400px",
            cursor: "pointer",
          })}
          onClick={handleClick}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mr: -1 }}
          >
            <Box display="flex" alignItems="center">
              <AutomationsOutline
                sx={(theme) => ({
                  fontSize: 22,
                  mr: 1,
                  color: theme.palette.fuchsia[150],
                })}
              />
              <Typography
                variant="label1"
                color="grey.80"
                sx={(theme) => ({
                  textTransform: "uppercase",
                  color: theme.palette.fuchsia[150],
                })}
              >
                LOGIC {"›"} {data.label}
              </Typography>
            </Box>
            <MenuIconButton
              icon={<MoreHorizontal />}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem onClick={handleClick}>
                {viewMode ? "Preview" : "Edit"}
              </MenuItem>
              {!viewMode && (
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setDialogOpen(true);
                  }}
                >
                  Delete
                </MenuItem>
              )}
            </MenuIconButton>
          </Box>
          <Box>
            <Typography
              variant="p2"
              color="grey.100"
              fontWeight="600"
              textTransform="uppercase"
            >
              {`${transformNodeToFormValues(data)?.ruleType || ""} › ${
                transformNodeToFormValues(data)?.event || ""
              }`}
            </Typography>
          </Box>
          <Box mt={2}>
            {configured ? (
              <Chip
                sx={(theme) => ({
                  height: theme.spacing(5),
                  backgroundColor: theme.palette.primary[100],
                  color: theme.palette.common.white,
                  mr: 1,
                  fontSize: theme.spacing(3),
                  lineHeight: theme.spacing(3),
                  "& .MuiChip-label": { fontWeight: 700 },
                })}
                label="Configured"
              />
            ) : (
              <Chip
                sx={(theme) => ({
                  height: theme.spacing(5),
                  backgroundColor: theme.palette.yellow[150],
                  color: theme.palette.common.white,
                  mr: 1,
                  fontSize: theme.spacing(3),
                  lineHeight: theme.spacing(3),
                  "& .MuiChip-label": { fontWeight: 700 },
                })}
                label="Not-Configured"
              />
            )}
          </Box>
          {/* {configured && (
            <>
              <Divider sx={{ mt: 4 }} />
              <Box mt={2}>{renderStatsContent()}</Box>
            </>
          )} */}
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="column" alignItems="center">
            <Divider
              orientation="vertical"
              sx={{
                height: "20px",
              }}
              color="grey.60"
            />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              sx={(theme) => ({
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[80]}`,
                padding: 3,
                borderRadius: 3,
                width: "90px",
              })}
            >
              <Typography
                variant="label1"
                color="grey.80"
                sx={{ textTransform: "capitalize", width: "100%" }}
                textAlign={"center"}
              >
                Yes
              </Typography>
              {/* {configured &&
                showNodeStats &&
                renderPathStats("positive", "Yes")} */}
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" alignItems="center">
            <Divider
              orientation="vertical"
              sx={{
                height: "20px",
              }}
              color="grey.60"
            />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              sx={(theme) => ({
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[80]}`,
                padding: 3,
                borderRadius: 3,
                width: "90px",
              })}
            >
              <Typography
                variant="label1"
                color="grey.80"
                sx={{ textTransform: "capitalize", width: "100%" }}
                textAlign={"center"}
              >
                No
              </Typography>
              {/* {configured && showNodeStats && renderPathStats("negative", "No")} */}
            </Box>
          </Box>
        </Box>
      </Box>
      <Handle
        type="source"
        position={Position.Bottom}
        id="left"
        style={{
          left: "10%",
          background: "#555",
          backgroundColor: "transparent",
          border: "none",
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="right"
        style={{
          left: "90%",
          background: "#555",
          backgroundColor: "transparent",
          border: "none",
        }}
      />
      <DeleteBranchDialog
        open={dialogOpen}
        onCancel={() => setDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default LogicNode;
